import { Card, PageHeader } from 'antd';
import moment, { Moment } from 'moment';
import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { getBookings } from '../../api/booking';
import { getOneWorkspace } from '../../api/workspaces';
import FulfilmentRate from '../../components/InspectorCompass/FulfilmentRate';
import StatusCharts from '../../components/InspectorCompass/StatusCharts';
import { FINISHED_STATUSES_INDEXES, STATUSES } from '../../const/booking';
import FiltersBar from './components/FiltersBar';
import { useBookingSnapshots } from './hooks/useBookingSnapshots';
import { useWorkspaceSearch } from './hooks/useWorkspaceSearch';
import { SnapshotGroupBy } from './interfaces';
import { DateRangeSelection, WorkspaceOption } from './models';

const InspectorCompass: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const defaultStart = useMemo(() => moment().startOf('day').toISOString(), []);
    const defaultEnd = useMemo(() => moment().endOf('day').toISOString(), []);

    const [dateRange, setDateRange] = useState<DateRangeSelection>({
        startDate: searchParams.get('startDate') || defaultStart,
        endDate: searchParams.get('endDate') || defaultEnd,
    });

    const initialWorkspaceID = useMemo(() => searchParams.get('workspaceID') || '', []);
    const [selectedWorkspaceID, setSelectedWorkspaceID] = useState<string>(initialWorkspaceID);
    const [loadingWorkspace, setLoadingWorkspace] = useState<boolean>(false);

    const [uncompletedBookings, setUncompletedBookings] = useState<number>(0);

    const [initialGroupBy, setInitialGroupBy] = useState<SnapshotGroupBy>(
        searchParams.get('groupBy') as SnapshotGroupBy || SnapshotGroupBy.HOUR,
    );
    const {
        options: workspaceOptions,
        loadingMore: workspaceLoadingMore,
        onSearch: onWorkspaceSearch,
        onScroll: onWorkspaceScroll,
        onDropdownVisibleChange,
        setOptions: setWorkspaceOptions,
    } = useWorkspaceSearch();

    const {
        snapshots, loading, fetchSnapshots, stats, total,
    } = useBookingSnapshots();

    useEffect(() => {
        const params: Record<string, string> = {};
        if (dateRange.startDate) params.startDate = dateRange.startDate;
        if (dateRange.endDate) params.endDate = dateRange.endDate;
        const urlParams = new URLSearchParams(params);
        if (selectedWorkspaceID) urlParams.set('workspaceID', selectedWorkspaceID);
        setSearchParams(urlParams);
    }, []);

    const handleGroupByChange = useCallback((value: SnapshotGroupBy) => {
        setInitialGroupBy(value as SnapshotGroupBy);
        const params = new URLSearchParams(searchParams);
        params.set('groupBy', value);
        setSearchParams(params);
    }, [setSearchParams]);

    const handleDateChange = useCallback((dates: [Moment | null, Moment | null] | null) => {
        if (dates && dates[0] && dates[1]) {
            const [start, end] = dates;
            setDateRange({
                startDate: start.startOf('day').toISOString(),
                endDate: end.endOf('day').toISOString(),
            });
            const params = new URLSearchParams(searchParams);
            params.set('startDate', start.startOf('day').toISOString());
            params.set('endDate', end.endOf('day').toISOString());
            if (selectedWorkspaceID) params.set('workspaceID', selectedWorkspaceID);
            setSearchParams(params);
            return;
        }
        setDateRange({ startDate: defaultStart, endDate: defaultEnd });
        const params = new URLSearchParams(searchParams);
        params.set('startDate', defaultStart);
        params.set('endDate', defaultEnd);
        if (selectedWorkspaceID) params.set('workspaceID', selectedWorkspaceID);
        setSearchParams(params);
    }, [defaultStart, defaultEnd, searchParams, selectedWorkspaceID, setSearchParams]);

    const handleStartSearch = useCallback(() => {
        const baseParams: Record<string, string> = {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
        };
        const urlParams = new URLSearchParams(baseParams);
        if (selectedWorkspaceID) urlParams.set('workspaceID', selectedWorkspaceID);
        setSearchParams(urlParams);
        fetchSnapshots({
            workspaceID: selectedWorkspaceID, startDate: dateRange.startDate, endDate: dateRange.endDate, groupBy: initialGroupBy,
        });
    }, [
        dateRange.endDate,
        dateRange.startDate,
        selectedWorkspaceID,
        setSearchParams,
        fetchSnapshots,
        initialGroupBy,
    ]);

    const handleWorkspaceChange = useCallback((id: string) => {
        setSelectedWorkspaceID(id);
        const params = new URLSearchParams(searchParams);
        if (id) params.set('workspaceID', id); else params.delete('workspaceID');
        params.set('startDate', dateRange.startDate);
        params.set('endDate', dateRange.endDate);
        setSearchParams(params);
    }, [searchParams, dateRange.startDate, dateRange.endDate, setSearchParams]);

    const fetchOneWorkspace = useCallback(async (id: string) => {
        setLoadingWorkspace(true);
        try {
            const { data } = await getOneWorkspace(id);
            const ws = data?.data;
            if (ws) {
                setWorkspaceOptions((prev: WorkspaceOption[]) => (prev.some((p) => p._id === selectedWorkspaceID)
                    ? prev
                    : [...prev, { _id: ws._id || selectedWorkspaceID, name: ws.name || selectedWorkspaceID }]));
            }
        } catch (err) {
            setWorkspaceOptions((prev: WorkspaceOption[]) => (prev.some((p) => p._id === selectedWorkspaceID)
                ? prev
                : [...prev, { _id: selectedWorkspaceID, name: selectedWorkspaceID }]));
        } finally {
            setLoadingWorkspace(false);
        }
    }, [setWorkspaceOptions]);

    const fetchUncompletedBookings = useCallback(async () => {
        await getBookings(
            {
                limit: 10,
                page: 1,
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
            },
            '',
            FINISHED_STATUSES_INDEXES.filter((status) => status !== STATUSES.COMPLETED.INDEX),
            selectedWorkspaceID,
        ).then(({ data }) => {
            setUncompletedBookings(data.data.total);
        }).catch((error) => {
            console.error(error);
        });
    }, [selectedWorkspaceID, dateRange.startDate, dateRange.endDate]);

    useEffect(() => {
        fetchSnapshots({
            workspaceID: selectedWorkspaceID, startDate: dateRange.startDate, endDate: dateRange.endDate, groupBy: initialGroupBy,
        });
    }, [
        selectedWorkspaceID,
        dateRange.startDate,
        dateRange.endDate,
        fetchSnapshots,
        initialGroupBy,
    ]);

    const rangePickerValue = useMemo(() => [moment(dateRange.startDate), moment(dateRange.endDate)], [dateRange.endDate, dateRange.startDate]);

    useEffect(() => {
        if (!selectedWorkspaceID) return;
        const exists = workspaceOptions.some((ws) => ws._id === selectedWorkspaceID);
        if (!exists) {
            fetchOneWorkspace(selectedWorkspaceID);
        }
    }, [selectedWorkspaceID, workspaceOptions, setWorkspaceOptions]);

    useEffect(() => {
        if (!selectedWorkspaceID) return;
        fetchUncompletedBookings();
    }, [selectedWorkspaceID, dateRange.startDate, dateRange.endDate]);

    return (
        <div>
            <PageHeader title="Inspector Compass" />
            <Card bordered style={{ marginBottom: 16 }}>
                <FiltersBar
                    rangeValue={rangePickerValue as any}
                    onDateChange={(date: any) => handleDateChange(date as any)}
                    disabledDate={(current: Moment) => current && current.isAfter(moment().endOf('day'))}
                    workspaceOptions={workspaceOptions as WorkspaceOption[]}
                    loadingWorkspaces={workspaceLoadingMore}
                    selectedWorkspaceID={selectedWorkspaceID}
                    groupBy={initialGroupBy}
                    onGroupByChange={handleGroupByChange}
                    onWorkspaceSearch={onWorkspaceSearch}
                    onWorkspaceScroll={onWorkspaceScroll}
                    onWorkspaceDropdownVisibleChange={onDropdownVisibleChange}
                    onWorkspaceChange={handleWorkspaceChange}
                    onReload={handleStartSearch}
                    loadingWorkspace={loadingWorkspace}
                />
            </Card>
            <StatusCharts stats={stats} total={total} snapshots={snapshots as any[]} loading={loading} groupBy={initialGroupBy} />
            <FulfilmentRate total={total} uncompletedBookings={uncompletedBookings} />
        </div>
    );
};

export default InspectorCompass;
